import React from 'react';
import { EntityIcon, isImageIcon } from './EntityIcon';
import { IconDecision, IconTodo, IconSparkle, IconArtifact, IconMessage } from './Icons';
import './TodayView.css';

// A compact avatar for a thread participant with a presence dot (online / away /
// offline) in the corner. Falls back to the first initial on a coloured disc
// when the user has no emoji/image avatar.
function PresenceAvatar({ person, status }) {
  const avatar = person.avatar;
  return (
    <span className="today-person" title={`${person.name} · ${status}`}>
      {avatar && isImageIcon(avatar) ? (
        <EntityIcon icon={avatar} name={person.name} size={22} className="today-person-avatar" />
      ) : avatar ? (
        <span className="today-person-avatar today-person-emoji">{avatar}</span>
      ) : (
        <span
          className="today-person-avatar today-person-initial"
          style={{ background: person.color || 'var(--accent)' }}
        >
          {(person.name || '?').charAt(0).toUpperCase()}
        </span>
      )}
      <span className={`today-person-dot ${status}`} aria-hidden="true" />
    </span>
  );
}

// The people cluster shown on each thread row: participants ordered
// online-first, capped to a few avatars with a "+N" overflow, and a summary
// label indicating how many are currently active. Communicates who is available
// in the thread and whether they're active or offline at a glance.
function ThreadPresence({ participants = [], onlineUsers = {} }) {
  if (!participants.length) return null;
  const statusOf = (p) => onlineUsers[p.id]?.status || 'offline';
  const rank = { online: 0, away: 1, offline: 2 };
  const ordered = [...participants].sort((a, b) => rank[statusOf(a)] - rank[statusOf(b)]);
  const onlineCount = ordered.filter((p) => statusOf(p) === 'online').length;
  const MAX = 3;
  const shown = ordered.slice(0, MAX);
  const overflow = ordered.length - shown.length;
  let label;
  if (onlineCount > 0) {
    label = `${onlineCount} active`;
  } else {
    label = ordered.length === 1 ? '1 person' : `${ordered.length} people`;
  }
  return (
    <span
      className="today-thread-people"
      aria-label={
        onlineCount > 0
          ? `${onlineCount} of ${ordered.length} active`
          : `${ordered.length} people, none active`
      }
    >
      <span className="today-thread-avatars">
        {shown.map((p) => (
          <PresenceAvatar key={p.id} person={p} status={statusOf(p)} />
        ))}
        {overflow > 0 && <span className="today-person-more">+{overflow}</span>}
      </span>
      <span className={`today-thread-people-label ${onlineCount > 0 ? 'active' : ''}`}>
        {label}
      </span>
    </span>
  );
}

// Time-of-day greeting for the Today header. Local time; the eyebrow above it
// carries the date.
function greeting(hour) {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function formatDay(now) {
  return now.toLocaleDateString([], {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

// A single Direction B todo row: circular checkbox + text (struck when done) +
// "thread · due X" meta. Read-only unless an `onToggle` is supplied (Phase 3
// wires the workspace toggle); the checkbox reflects `completed` either way.
export function TodoRow({ todo, onToggle, onOpenThread, onOpenDetail }) {
  const done = !!todo.completed;
  const interactive = typeof onToggle === 'function';
  // When a detail reader is wired, the body opens the todo "to the fore" and a
  // provenance chip links to its source thread. Otherwise the body links to the
  // thread directly (the Today "Needs you" behaviour).
  const bodyOpensDetail = typeof onOpenDetail === 'function';
  return (
    <div className={`today-todo-row ${done ? 'done' : ''}`}>
      <button
        type="button"
        className={`today-todo-check ${done ? 'checked' : ''}`}
        onClick={interactive ? () => onToggle(todo) : undefined}
        disabled={!interactive}
        aria-pressed={done}
        aria-label={done ? 'Mark not done' : 'Mark done'}
      >
        {done ? '✓' : ''}
      </button>
      <button
        type="button"
        className="today-todo-body"
        onClick={
          bodyOpensDetail
            ? () => onOpenDetail(todo)
            : onOpenThread
              ? () => onOpenThread(todo.roomId)
              : undefined
        }
        disabled={!bodyOpensDetail && !onOpenThread}
        title={bodyOpensDetail ? 'Open to read' : undefined}
      >
        <span className="today-todo-text">{todo.text}</span>
        {todo.due && <span className="today-todo-meta">due {todo.due}</span>}
      </button>
      {todo.roomName && onOpenThread && (
        <button
          type="button"
          className="today-todo-thread"
          onClick={() => onOpenThread(todo.roomId)}
          title={`Open “${todo.roomName}”`}
        >
          <IconMessage size={12} />
          <span>{todo.roomName}</span>
        </button>
      )}
    </div>
  );
}

// The Today stream: greeting, an AI briefing card (a token-free factual line in
// this phase — Phase 4 swaps in the AI paragraph), a "Needs you" card of the
// caller's open todos, a Threads card with last-message previews + unread dots,
// and Decisions / Todos shortcut cards. Rendered as the Lobby's default view.
function TodayView({
  user,
  rooms = [],
  todos = [],
  decisionsTotal = 0,
  artefactsTotal = 0,
  lastSeenCounts = {},
  onlineUsers = {},
  caughtUp = false,
  briefing: briefingProp,
  activeThreadId = null,
  onMarkCaughtUp,
  onJoinThread,
  onGoToView,
  onToggleTodo,
  onNewThread,
}) {
  const now = new Date();
  const openTodos = todos.filter((t) => !t.completed);
  // "Needs you" = the caller's own open todos. Todos have no assignee column
  // yet, so ownership is matched on the creator's display name.
  const myOpen = openTodos.filter((t) => t.createdBy && t.createdBy === user?.name);

  const unreadByRoom = (room) => {
    const seen = lastSeenCounts[room.id] || 0;
    const visible = room.chatMessageCount ?? room.messageCount ?? 0;
    return Math.max(0, visible - seen);
  };
  const totalUnread = rooms.reduce((s, r) => s + unreadByRoom(r), 0);

  // Newest-activity-first: threads with a recent last message rise to the top,
  // falling back to creation time for never-used threads.
  const sortedThreads = [...rooms].sort((a, b) => {
    const at = Date.parse(a.lastMessage?.at || a.createdAt || '') || 0;
    const bt = Date.parse(b.lastMessage?.at || b.createdAt || '') || 0;
    return bt - at;
  });

  // Prefer the server briefing (AI paragraph, or its own facts fallback). Before
  // it lands, show an equivalent line computed from the counts on hand.
  const localBriefing = (() => {
    const bits = [];
    if (myOpen.length) {
      bits.push(`${myOpen.length} to-do${myOpen.length === 1 ? '' : 's'} waiting on you`);
    }
    if (totalUnread) {
      bits.push(`${totalUnread} unread message${totalUnread === 1 ? '' : 's'}`);
    }
    if (decisionsTotal) {
      bits.push(`${decisionsTotal} decision${decisionsTotal === 1 ? '' : 's'} on record`);
    }
    if (!bits.length) return 'You’re all caught up — nothing needs you right now.';
    const joined =
      bits.length === 1 ? bits[0] : `${bits.slice(0, -1).join(', ')} and ${bits[bits.length - 1]}`;
    return `Here’s where things stand: ${joined}.`;
  })();
  const briefing = briefingProp || localBriefing;

  return (
    <div className="today-view">
      <div className="today-eyebrow">{formatDay(now)}</div>
      <h2 className="today-greeting">
        {greeting(now.getHours())}
        {user?.name ? `, ${user.name.split(' ')[0]}` : ''}
      </h2>

      {/* Briefing card (hero) — collapses to an ambient "caught up" line once
          the user marks the workspace caught up, until newer activity appears. */}
      {caughtUp ? (
        <div className="today-ambient">
          <span className="today-ambient-mark" aria-hidden="true">
            <IconSparkle size={14} />
          </span>
          You’re all caught up
        </div>
      ) : (
        <div className="today-card today-briefing">
          <div className="today-briefing-head">
            <span className="today-briefing-mark" aria-hidden="true">
              <IconSparkle size={15} />
            </span>
            <span className="today-briefing-title">Your briefing</span>
            <span className="today-briefing-ai">Aimy · AI</span>
          </div>
          <p className="today-briefing-body">{briefing}</p>
          <div className="today-briefing-actions">
            <button
              type="button"
              className="today-briefing-btn"
              onClick={() => onMarkCaughtUp && onMarkCaughtUp()}
            >
              Mark caught up
            </button>
            {decisionsTotal > 0 && (
              <button
                type="button"
                className="today-briefing-btn ghost"
                onClick={() => onGoToView && onGoToView('decisions')}
              >
                {decisionsTotal} decision{decisionsTotal === 1 ? '' : 's'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Needs you */}
      {myOpen.length > 0 && (
        <section className="today-section">
          <div className="today-section-label">Needs you</div>
          <div className="today-card today-list">
            {myOpen.map((t) => (
              <TodoRow key={t.id} todo={t} onToggle={onToggleTodo} onOpenThread={onJoinThread} />
            ))}
          </div>
        </section>
      )}

      {/* Threads */}
      <section className="today-section">
        <div className="today-section-label today-section-label-row">
          <span>Threads</span>
          {onGoToView && sortedThreads.length > 0 && (
            <button type="button" className="today-see-all" onClick={() => onGoToView('threads')}>
              All threads ›
            </button>
          )}
        </div>
        <div className="today-card today-list">
          {sortedThreads.length === 0 ? (
            <div className="today-empty today-empty-threads">
              <span>No threads yet.</span>
              {onNewThread && (
                <button type="button" className="today-new-thread-cta" onClick={onNewThread}>
                  + New thread
                </button>
              )}
            </div>
          ) : (
            sortedThreads.map((room) => {
              const unread = unreadByRoom(room);
              const preview = room.lastMessage
                ? `${room.lastMessage.sender}: ${room.lastMessage.snippet}`
                : 'No messages yet';
              return (
                <button
                  key={room.id}
                  type="button"
                  className={`today-thread-row ${room.id === activeThreadId ? 'active' : ''}`}
                  aria-current={room.id === activeThreadId ? 'true' : undefined}
                  onClick={() => onJoinThread(room.id)}
                >
                  <EntityIcon icon={room.icon} name={room.name} size={23} />
                  <span className="today-thread-body">
                    <span className="today-thread-name">{room.name}</span>
                    <span className="today-thread-preview">{preview}</span>
                  </span>
                  <ThreadPresence participants={room.participants} onlineUsers={onlineUsers} />
                  {!caughtUp && unread > 0 && (
                    <span className="today-thread-dot" aria-label="Unread" />
                  )}
                </button>
              );
            })
          )}
        </div>
      </section>

      {/* Shortcut cards */}
      <div className="today-shortcuts">
        <button
          type="button"
          className="today-card today-shortcut"
          onClick={() => onGoToView && onGoToView('decisions')}
        >
          <span className="today-shortcut-icon decisions" aria-hidden="true">
            <IconDecision size={18} />
          </span>
          <span className="today-shortcut-title">Decisions</span>
          <span className="today-shortcut-meta">{decisionsTotal} tracked</span>
        </button>
        <button
          type="button"
          className="today-card today-shortcut"
          onClick={() => onGoToView && onGoToView('todos')}
        >
          <span className="today-shortcut-icon todos" aria-hidden="true">
            <IconTodo size={18} />
          </span>
          <span className="today-shortcut-title">Todos</span>
          <span className="today-shortcut-meta">{openTodos.length} open</span>
        </button>
        <button
          type="button"
          className="today-card today-shortcut"
          onClick={() => onGoToView && onGoToView('artifacts')}
        >
          <span className="today-shortcut-icon artefacts" aria-hidden="true">
            <IconArtifact size={18} />
          </span>
          <span className="today-shortcut-title">Artefacts</span>
          <span className="today-shortcut-meta">{artefactsTotal} saved</span>
        </button>
      </div>
    </div>
  );
}

export default TodayView;
